import { CoreApi, Product, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { isFrameSuitableForPullOutInterior } from '@inter-ikea-storageone/range-platsa';
import { ComponentIds } from '@inter-ikea-core/core';
import { noProductsWithTypesForSelectedEntity } from 'rangeConfig/shared/rules/noneOptionSelectedRules';

export const isOpenFrame = (coreApi: CoreApi, selectedEntityId: number): boolean => {
  const entity = coreApi.entities.find(selectedEntityId);
  if (!entity) {
    return false;
  }
  return coreApi.entities.isOneOfProductTypes(entity, [
    ProductType.PLATSA_OPEN_FRAME_SHELVES,
    ProductType.PLATSA_OPEN_FRAME_HANGING,
    ProductType.PLATSA_OPEN_FRAME_SHOES
  ]);
};

export const isNotOpenFrame = (coreApi: CoreApi, selectedEntityId: number): boolean => {
  return !isOpenFrame(coreApi, selectedEntityId);
};

export const onlyRegularFramesInScene = (coreApi: CoreApi) => {
  const framesinScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const normalFrames = framesinScene.every(
    entity =>
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.PLATSA_FRAME
  );

  return normalFrames;
};

export const onlyOpenFramesInScene = (coreApi: CoreApi) => {
  const framesinScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const openFrames = framesinScene.every(entity => isOpenFrame(coreApi, entity.id));

  return framesinScene.length > 0 && openFrames;
};

export const regularFramesInScene = (coreApi: CoreApi) => {
  const framesinScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const normalFrames = framesinScene.filter(
    entity =>
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.PLATSA_FRAME
  );

  return (framesinScene.length > 0 && normalFrames.length > 0) || framesinScene.length === 0;
};

export const hideSlidingDoorInlineMessage = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const doesFrameContainSlidingDoor =
    selectedEntity &&
    coreApi.entities.getChildrenWithProductType(
      selectedEntity,
      ProductType.PLATSA_SLIDING_DOOR_ASM || ProductType.PLATSA_SLIDING_DOOR_FRONT
    )[0];

  const getSelectedProductType = selectedEntity && coreApi.entities.getProductType(selectedEntity);

  if (getSelectedProductType === ProductType.PLATSA_FRAME) {
    return !doesFrameContainSlidingDoor;
  }

  const isSlidingDoor =
    getSelectedProductType ===
    (ProductType.PLATSA_SLIDING_DOOR_ASM || ProductType.PLATSA_SLIDING_DOOR_FRONT);

  return !isSlidingDoor;
};

export const hideOpeningDirectionWidget = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) return true;
  const width = coreApi.entities.getEntityComSize(selectedEntity)?.width;
  return (
    coreApi.entities.isOneOfProductTypes(selectedEntity, [
      ProductType.PLATSA_DRAWER_ASM,
      ProductType.PLATSA_SLIDING_DOOR_ASM
    ]) || width === 400
  );
};

export const hideSlidingDoorPullOutMessage = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (selectedEntity && selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    return isFrameSuitableForPullOutInterior(selectedEntity, coreApi);
  }
  return true;
};

// Error to be displayed when user tries to add doors but the solution is an open frame
export const hidePlatsaAddingFrontToOpenFrameErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const openFrameTypes = [
    ProductType.PLATSA_OPEN_FRAME_SHELVES,
    ProductType.PLATSA_OPEN_FRAME_HANGING,
    ProductType.PLATSA_OPEN_FRAME_SHOES
  ];
  const openFramesInScene = framesInScene?.filter(f => {
    const type = f.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType;
    return type && openFrameTypes.includes(type);
  });
  const onlyOpenFrames =
    openFramesInScene.length > 0 && openFramesInScene.length === framesInScene.length;
  return !onlyOpenFrames;
};

// Error to be displayed when user tries to add handles but the solution is an open frame
export const hidePlatsaAddingHandleToOpenFrameErrorMessage = (coreApi: CoreApi) => {
  return hidePlatsaAddingFrontToOpenFrameErrorMessage(coreApi);
};

// Error to be displayed when user tries to add sliding doors with only one/different frames in the scene
export const hidePlatsaSlidingDoorsNotPossibleErrorMessage = (
  coreApi: CoreApi,
  products: Product[]
) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const framesThatCanHaveSlidingDoors = framesInScene.filter(frame => {
    const frameSize = coreApi.entities.getEntityComSize(frame);
    const slidingDoorsInFrameSize = products.find(
      p =>
        p.type === ProductType.PLATSA_SLIDING_DOOR_ASM &&
        p.communicatedDimensions.width === frameSize?.width &&
        p.communicatedDimensions.height === frameSize?.height
    );
    const alignedFrames = coreApi.entities.getAlignedFrames(
      frame,
      'align-frame-left',
      'align-frame-right',
      true,
      true
    );
    return slidingDoorsInFrameSize && (alignedFrames.left || alignedFrames.right);
  });

  const slidingDoorsNotPossible =
    framesInScene?.length > 0 && framesThatCanHaveSlidingDoors?.length === 0;

  return !slidingDoorsNotPossible;
};

export const isLightOnFrame = (products: Product, coreApi: CoreApi): boolean =>
  !noProductsWithTypesForSelectedEntity([ProductType.PLATSA_INNER_LIGHT], true)(coreApi);
